import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatedPostBanner } from "@/components/portal/AnimatedPostBanner";
import { cn } from "@/lib/utils";
import { Link, useSearchParams } from "react-router-dom";
import { EmptyState, EmptyPresets } from "@/components/ds";
import { supabase } from "@/integrations/supabase/client";
import { Search, Eye, ArrowUpRight } from "lucide-react";
import { NewsletterSubscribeButton } from "@/components/portal/NewsletterSubscribeButton";
import { PageSEO } from "@/components/portal/PageSEO";
import { imgUrl, imgSrcSet } from "@/lib/img";

type Article = {
  id: string;
  slug: string;
  title: string;
  excerpt?: string | null;
  thumbnail?: string | null;
  category?: string | null;
  author_name?: string | null;
  author_avatar?: string | null;
  reading_time?: number | null;
  views?: number | null;
  published_at?: string | null;
  featured?: boolean | null;
};

type SortKey = "recent" | "popular";
const MAIN_TAGS_LIMIT = 5; // + "Todos" = 6 tags principais visíveis

async function fetchAllArticles(): Promise<Article[]> {
  const nowIso = new Date().toISOString();
  const { data, error } = await (supabase as any)
    .from("articles_public")
    .select("id,slug,title,excerpt,thumbnail,category,author_name,author_avatar,reading_time,views,published_at,featured")
    .or(`published_at.is.null,published_at.lte.${nowIso}`)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(120);
  if (error) throw error;
  return (data ?? []) as Article[];
}

function formatDate(d?: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function MetaLine({ a, compact = false }: { a: Article; compact?: boolean }) {
  const parts = [
    a.author_name,
    a.published_at && formatDate(a.published_at),
  ].filter(Boolean);
  return (
    <div className={cn("flex items-center gap-2 text-foreground/60 min-w-0", compact ? "text-[11px]" : "text-xs")}>
      {a.author_avatar ? (
        <img src={imgUrl(a.author_avatar, 48)} srcSet={imgSrcSet(a.author_avatar, [24, 48]) || undefined} sizes="24px" alt="" width={24} height={24} loading="lazy" decoding="async" className={cn("rounded-full object-cover shrink-0", compact ? "w-5 h-5" : "w-6 h-6")} />
      ) : (
        <span className={cn("rounded-full bg-primary/15 shrink-0", compact ? "w-5 h-5" : "w-6 h-6")} />
      )}
      <span className="truncate">{parts.join(" · ")}</span>
    </div>
  );
}

function CategoryChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[1.5px] text-primary">
      {label}
    </span>
  );
}

export default function PortalBlog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const [cat, setCat] = useState<string>("Todos");
  const [query, setQuery] = useState(initialQ);
  const [sort, setSort] = useState<SortKey>("recent");
  useEffect(() => {
    const q = searchParams.get("q") ?? "";
    setQuery(q);
    if (q) setCat("Todos");
  }, [searchParams]);
  const clearSearch = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
    setQuery("");
  };

  const [showAllCats, setShowAllCats] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;


  const { data, isLoading } = useQuery({
    queryKey: ["articles", "blog-home"],
    queryFn: fetchAllArticles,
    staleTime: 0,
    gcTime: 60_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const articles = data ?? [];

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        (a.excerpt ?? "").toLowerCase().includes(q) ||
        (a.category ?? "").toLowerCase().includes(q),
    );
  }, [articles, query]);

  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    searched.forEach((a) => {
      if (a.category) map.set(a.category, (map.get(a.category) ?? 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [searched]);


  const filtered = useMemo(() => {
    let list = searched;
    if (cat !== "Todos") list = list.filter((a) => a.category === cat);

    if (sort === "popular") {
      list = [...list].sort((a, b) => {
        const dv = (b.views ?? 0) - (a.views ?? 0);
        if (dv !== 0) return dv;
        const df = (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        if (df !== 0) return df;
        return (b.reading_time ?? 0) - (a.reading_time ?? 0);
      });
    } else {
      list = [...list].sort((a, b) => {
        const ta = a.published_at ? new Date(a.published_at).getTime() : 0;
        const tb = b.published_at ? new Date(b.published_at).getTime() : 0;
        return tb - ta;
      });
    }
    return list;
  }, [searched, cat, sort]);

  const featured = filtered[0];
  const secondary = filtered.slice(1, 3);
  const rest = filtered.slice(3);
  const totalPages = Math.max(1, Math.ceil(rest.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRest = rest.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [cat, query, sort]);

  return (

    <>
      <PageSEO
        path="/blog"
        canonicalPath="/"
        title="Blog — Artigos de Comércio Exterior, IA e Logística"
        description="Artigos sobre IA aplicada, inteligência comercial, operações e panorama macro do comex brasileiro."
      />
      {query.trim() ? (
        <section className="mb-8">
          <div className="text-[11px] font-semibold tracking-[1.8px] uppercase text-primary mb-3">Busca</div>
          <h1 className="font-display text-[36px] max-md:text-[26px] font-normal text-foreground leading-tight mb-3">
            Resultados para <span className="text-primary">“{query.trim()}”</span>
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[13px] text-foreground/60">
              {filtered.length} {filtered.length === 1 ? "artigo encontrado" : "artigos encontrados"}
            </span>
            <button
              type="button"
              onClick={clearSearch}
              className="text-[12px] font-medium px-3 py-1.5 rounded-full border border-foreground/15 text-foreground/70 hover:bg-foreground/5 transition-colors"
            >
              Limpar busca
            </button>
          </div>
        </section>
      ) : (
        /* Editorial Hero */
        <section className="mb-10 max-md:mb-8 grid grid-cols-[1.5fr_1fr] max-lg:grid-cols-1 gap-10 max-lg:gap-8 items-end">
          <div>
            <div className="text-[11px] font-semibold tracking-[1.8px] uppercase text-primary mb-3">Blog Logcomex.ai</div>
            <h1 className="font-display text-[48px] max-md:text-[32px] font-normal text-foreground leading-[1.05] mb-4 max-w-3xl">
              Inteligência, opinião e insights sobre <span className="text-primary">comércio exterior e IA</span>
            </h1>
            <p className="text-[15px] font-light text-foreground/60 leading-relaxed max-w-2xl mb-7">
              Artigos com insight proprietário, análises com posição e conteúdo interativo — escrito por quem vive comex todos os dias.
            </p>
          </div>

          {/* Newsletter signup */}
          <NewsletterCard />
        </section>
      )}



      {/* Sticky filter bar */}
      <div className="sticky top-0 z-20 -mx-4 px-4 md:-mx-6 md:px-6 bg-background/85 backdrop-blur-md border-b border-foreground/[0.06] mb-8">
        <div className="flex items-start justify-between gap-4 py-3 max-md:flex-col max-md:items-stretch">
          <div className="min-w-0 flex-1 max-w-full overflow-hidden">
            <div className="flex flex-wrap gap-2 items-center max-w-full">
              <CategoryTab label="Todos" count={searched.length} active={cat === "Todos"} onClick={() => setCat("Todos")} />
              {categoryCounts.slice(0, MAIN_TAGS_LIMIT).map(([name, count]) => (
                <CategoryTab key={name} label={name} count={count} active={cat === name} onClick={() => setCat(name)} />
              ))}
              {categoryCounts.length > MAIN_TAGS_LIMIT && (
                <button
                  type="button"
                  onClick={() => setShowAllCats((v) => !v)}
                  className="shrink-0 text-[12px] font-medium px-3 py-1.5 rounded-full border border-foreground/15 text-foreground/70 hover:bg-foreground/5 transition-colors"
                  aria-expanded={showAllCats}
                  aria-label={showAllCats ? "Ocultar tags" : "Mostrar mais tags"}
                >
                  {showAllCats ? "−" : "+"}
                </button>
              )}
            </div>
            {showAllCats && categoryCounts.length > MAIN_TAGS_LIMIT && (
              <div className="flex flex-wrap gap-2 pt-3 max-w-full">
                {categoryCounts.slice(MAIN_TAGS_LIMIT).map(([name, count]) => (
                  <CategoryTab key={name} label={name} count={count} active={cat === name} onClick={() => setCat(name)} />
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <SortTab active={sort === "recent"} onClick={() => setSort("recent")}>Mais recentes</SortTab>
            <SortTab active={sort === "popular"} onClick={() => setSort("popular")}>Mais lidos</SortTab>
          </div>
        </div>
      </div>

      {isLoading ? (
        /* Skeleton com a mesma altura da lista final — evita layout shift (CLS). */
        <div aria-busy="true" aria-label="Carregando artigos">
          <div className="mb-10 grid grid-cols-[1.15fr_1fr] max-lg:grid-cols-1 gap-0 rounded-2xl overflow-hidden bg-card border border-foreground/[0.08]">
            <div className="aspect-[4/3] max-lg:aspect-[16/9] bg-muted animate-pulse" />
            <div className="p-10 max-lg:p-7 max-md:p-6 space-y-4">
              <div className="h-3 w-24 rounded bg-muted animate-pulse" />
              <div className="h-8 w-3/4 rounded bg-muted animate-pulse" />
              <div className="h-4 w-full rounded bg-muted animate-pulse" />
              <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
            </div>
          </div>
          <div className="grid grid-cols-2 max-md:grid-cols-1 gap-5 mb-12">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-2xl overflow-hidden bg-card border border-foreground/[0.08]">
                <div className="aspect-[16/9] bg-muted animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 max-lg:grid-cols-2 max-md:grid-cols-1 gap-5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-2xl overflow-hidden bg-card border border-foreground/[0.08]">
                <div className="aspect-[16/9] bg-muted animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>

      ) : filtered.length === 0 ? (
        <EmptyState
          {...EmptyPresets.noResults("PT-BR")}
          title={query ? "Nenhum artigo encontrado" : "Nenhum artigo nesta categoria"}
          description={query ? `Não encontramos nada para "${query}". Tente outro termo.` : "Explore outras categorias enquanto publicamos novidades."}
        />
      ) : (
        <>
          {/* Editorial featured */}
          {featured && (
            <Link to={`/artigo/${featured.slug}`} className="block mb-10 group">
              <article className="grid grid-cols-[1.15fr_1fr] max-lg:grid-cols-1 gap-0 rounded-2xl overflow-hidden bg-card border border-foreground/[0.08] transition-all duration-300 hover:shadow-modal hover:border-primary/20">
                <div className="aspect-[4/3] max-lg:aspect-[16/9] bg-muted overflow-hidden">
                  {featured.thumbnail ? (
                    <img
                      src={imgUrl(featured.thumbnail, 800)}
                      srcSet={imgSrcSet(featured.thumbnail, [480, 800, 1200]) || undefined}
                      sizes="(max-width: 768px) 100vw, 800px"
                      width={1200}
                      height={675}
                      alt={featured.title}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-purple" />
                  )}
                </div>
                <div className="p-10 max-lg:p-7 max-md:p-6 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    {featured.category && <CategoryChip label={featured.category} />}
                    <span className="text-[10px] font-semibold uppercase tracking-[1.5px] text-foreground/65">Destaque</span>
                  </div>
                  <h2 className="font-display text-[32px] max-lg:text-[26px] max-md:text-[22px] font-medium text-foreground leading-[1.15] mb-4">
                    {featured.title}
                  </h2>
                  {featured.excerpt && (
                    <p className="text-[14px] font-light text-foreground/65 leading-relaxed mb-6 line-clamp-3">{featured.excerpt}</p>
                  )}
                  <MetaLine a={featured} />
                  <div className="inline-flex items-center gap-1.5 text-xs font-medium text-primary mt-5 group-hover:gap-2.5 transition-all">
                    Ler artigo <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </article>
            </Link>
          )}

          {/* Secondary featured (2 cols) */}
          {secondary.length > 0 && (
            <section className="grid grid-cols-2 max-md:grid-cols-1 gap-5 mb-12">
              {secondary.map((p) => (
                <ArticleCard key={p.id} a={p} size="md" />
              ))}
            </section>
          )}

          {/* Rest grid */}
          {rest.length > 0 && (
            <section className="mb-4 scroll-mt-24" id="todos-artigos">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-xl font-medium text-foreground">Todos os artigos</h2>
                <span className="text-xs text-foreground/60">{filtered.length} publicados</span>
              </div>

              <div className="grid grid-cols-3 max-lg:grid-cols-2 max-md:grid-cols-1 gap-5">
                {pagedRest.map((p) => (
                  <ArticleCard key={p.id} a={p} size="sm" />
                ))}
              </div>
              {totalPages > 1 && (
                <nav className="flex items-center justify-center gap-1.5 mt-10" aria-label="Paginação">
                  <button
                    type="button"
                    onClick={() => { setPage((p) => Math.max(1, p - 1)); document.getElementById("todos-artigos")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    disabled={currentPage === 1}
                    className="h-9 px-3 rounded-full text-[12px] font-medium border border-foreground/15 text-foreground/70 hover:bg-foreground/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => { setPage(n); document.getElementById("todos-artigos")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                      aria-current={n === currentPage ? "page" : undefined}
                      className={cn(
                        "h-9 min-w-9 px-3 rounded-full text-[12px] font-medium transition-colors",
                        n === currentPage
                          ? "bg-primary text-primary-foreground"
                          : "border border-foreground/15 text-foreground/70 hover:bg-foreground/5",
                      )}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); document.getElementById("todos-artigos")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    disabled={currentPage === totalPages}
                    className="h-9 px-3 rounded-full text-[12px] font-medium border border-foreground/15 text-foreground/70 hover:bg-foreground/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Próxima
                  </button>
                </nav>
              )}
            </section>
          )}

        </>
      )}

      <div className="px-6 max-md:px-4">
        <AnimatedPostBanner />
      </div>
    </>
  );
}

function NewsletterCard() {
  return (
    <aside className="rounded-2xl bg-primary/[0.06] border border-primary/15 p-7 max-md:p-6">
      <h2 className="font-display text-[22px] font-medium text-foreground leading-snug mb-2">
        Assine a newsletter da Logcomex.ai
      </h2>
      <p className="text-sm font-light text-foreground/65 leading-relaxed mb-5">
        Receba toda semana a curadoria de artigos, alertas regulatórios e indicadores de comex.
      </p>
      <NewsletterSubscribeButton source="blog_sidebar" label="Assinar" />
      
    </aside>
  );
}



function CategoryTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "shrink-0 px-4 py-2 rounded-full text-xs font-medium border transition-all whitespace-nowrap flex items-center gap-2",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-foreground/65 border-foreground/10 hover:border-primary/40 hover:text-primary",
      )}
    >
      <span>{label}</span>
      <span className={cn("text-[10px] tabular-nums", active ? "text-primary-foreground/70" : "text-foreground/60")}>
        {count}
      </span>
    </button>
  );
}

function SortTab({ active, onClick, children }: { active?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
        active ? "text-primary bg-primary/10" : "text-foreground/65 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function ArticleCard({ a, size = "sm" }: { a: Article; size?: "sm" | "md" }) {
  return (
    <Link to={`/artigo/${a.slug}`} className="group">
      <article className="rounded-2xl overflow-hidden bg-card border border-foreground/[0.08] cursor-pointer transition-all duration-300 hover:shadow-card hover:-translate-y-1 hover:border-primary/20 h-full flex flex-col">
        <div className="aspect-[16/9] bg-muted overflow-hidden">
          {a.thumbnail ? (
            <img
              src={imgUrl(a.thumbnail, 640)}
              srcSet={imgSrcSet(a.thumbnail, [320, 480, 640]) || undefined}
              sizes="(max-width: 768px) 100vw, 400px"
              width={640}
              height={360}
              alt={a.title}
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-purple" />
          )}
        </div>
        <div className={cn("flex-1 flex flex-col", size === "md" ? "p-6" : "p-5")}>
          {a.category && (
            <div className="mb-2">
              <CategoryChip label={a.category} />
            </div>
          )}
          <h3
            className={cn(
              "font-display font-medium text-foreground leading-snug mb-2",
              size === "md" ? "text-[22px]" : "text-[17px]",
            )}
          >
            {a.title}
          </h3>
          {a.excerpt && (
            <p className="text-[13px] font-light text-foreground/65 leading-relaxed mb-4 flex-1 line-clamp-2">
              {a.excerpt}
            </p>
          )}
          <div className="flex items-center justify-between gap-3 mt-auto">
            <MetaLine a={a} compact />
            <div className="flex items-center gap-3 text-[11px] text-foreground/60 shrink-0">
              {a.views ? (
                <span className="inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {a.views}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}
